<template>
  <div
    v-if="queueData.length > 0"
    class="queue-widget"
  >
 
    <div class="queue-header" ref="headerRef">
      <div class="header" :style="{ 
        color: queueConfig.numberHeaderColor,
        fontSize: headerFontSize + 'px !important'
      }">
        {{ numberTxt.toUpperCase() }}
      </div>
      <div class="header" :style="{ 
        color: queueConfig.counterHeaderColor,
        fontSize: headerFontSize + 'px !important'
      }">
        {{ counterTxt.toUpperCase() }}
      </div>
    </div>
    <div class="queue-body" :class="{ 'centered-few': isFewRows }">
      <div
        v-for="item in queueData"
        :key="item.id"
        class="queue-row"
      >
        <div
          class="ticket"
          :style="{
            color: queueConfig.numberTxtColor,
            background: queueConfig.numberBgColor,
            borderRadius:
              typeof borderRadius === 'number'
                ? borderRadius + 'px'
                : borderRadius,
            fontSize: ticketFontSize + 'px !important' 
          }"
        >
         {{ item.ticket }}
        </div>
        <div
          class="counter"
          :style="{
            color: queueConfig.counterTxtColor,
            background: queueConfig.counterBgColor,
            fontSize: ticketFontSize + 'px !important' 
          }"
        >
          {{ item.counter }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: "QueueDefaultWidget",
};
</script>

<script setup lang="ts">
import { ref, defineProps, computed, onMounted, nextTick } from "vue";
const props = defineProps<{
  queueData: Array<any>;
  queueConfig: any;
  borderRadius: number;
  config: any;
  numberTxt: string;
  counterTxt: string;
}>();

const headerRef = ref<HTMLElement | null>(null);
const widgetWidth = computed(() => props.config.width || 400);
const widgetHeight = computed(() => props.config.height || 400);
const rowCount = computed(() => props.queueData.length || 1);
const rowGap = 8;

const headerHeight = ref(50); 

onMounted(async () => {
  await nextTick();
  if (headerRef.value) {
    headerHeight.value = headerRef.value.getBoundingClientRect().height;
  }
});

const isFewRows = computed(() => rowCount.value <= 2);
const availableHeight = computed(() => widgetHeight.value - headerHeight.value);
const squareBoxSize = computed(() => {
  const w = widgetWidth.value / 2;
  const h = (availableHeight.value - (rowCount.value - 1) * rowGap) / rowCount.value;
  const base = Math.min(w, h);
  return isFewRows.value ? base * 0.5 : base;
});

// Responsive header font size
const headerFontSize = computed(() => {
  const w = widgetWidth.value;
  const h = headerHeight.value;
  // Use the smaller of 10% of width or 60% of header height, with reasonable min/max
  return Math.max(18, Math.min(55, Math.min(w * 0.10, h * 0.54)));
});

// Responsive ticket/counter font size
const ticketFontSize = computed(() => {
  const w = widgetWidth.value / 2; // each cell is half the width
  const h = (availableHeight.value - (rowCount.value - 1) * rowGap) / rowCount.value;
  // Use the smaller of 40% of row height or 40% of cell width, with min/max
  return Math.max(18, Math.min(75, Math.min(h * 0.4, w * 0.4)));
});
</script>

<style scoped>
.queue-widget {
  background: transparent;
  width: 100%;
  height: 100%;
  display: flex !important;
  flex-direction: column;
}

.queue-header {
  display: grid !important;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  margin-bottom: 1vw;
}

.header {
  text-align: center;
  font-weight: 400;
  padding: 0.5vw 0 0 0;
}



.queue-body {
  display: flex !important;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 100%;
  justify-content: flex-start;
}

.centered-few {
  height: auto !important;
  justify-content: flex-start !important;
}

.queue-row {
  display: grid !important;
  grid-template-columns: 1fr 1fr;
  gap: 1vw;
  width: 100%;
  align-items: stretch;
  min-height: 0;
  overflow: hidden;
}

/*flex: 1 1 0 for 3+ rows */
.queue-body:not(.centered-few) .queue-row {
  flex: 1 1 0 !important;
}

.ticket,
.counter {
  display: flex !important;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  min-width: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

/* large screens */
/* @media (min-width: 260px) {
  .ticket,
  .counter {
    font-size: calc(2vw + 0.5rem) !important;
  }
} */

.centered-few .ticket,
.centered-few .counter {
  display: flex !important;
  align-items: center;
  justify-content: center;
}

/* @media (min-width: 260px) {
  .centered-few .ticket,
  .centered-few .counter {
    font-size: calc(2vw + 0.5rem);
  }
} */
</style>